/**
 * =============================================================================
 * SPEQ55 RFP Pro - Parameter Sets API Functions
 * =============================================================================
 *
 * WHAT THIS FILE DOES
 * -------------------
 * Thin axios wrappers around every /api/parameter-sets endpoint. Each
 * function takes the domain-level args (id, payload) and returns just the
 * meaningful slice of the response body (the parameterSet object or array)
 * so call sites don't have to pluck `.data.parameterSet` every time.
 *
 * WHY IT EXISTS
 * -------------
 * Keeping every HTTP call for a resource in one module means:
 *   1. React components stay free of URL strings, headers, error-shape
 *      knowledge — they just await a function and get a plain JS value.
 *   2. Adding auth headers, retries, or interceptors happens once in the
 *      shared axios client (./client.js) and the whole app inherits it.
 *   3. The shape of the returned object is documented in one place via
 *      JSDoc, giving editors type-aware autocompletion.
 *
 * HOW IT WORKS
 * ------------
 * All network calls go through get/post/put/del from ./client.js, which
 * is a pre-configured axios instance with baseURL `/api`, credentials,
 * and the auth token interceptor. Each function below is a one- or
 * two-line wrapper that unwraps `response.data.parameterSet[s]`.
 *
 * DEPENDENCIES
 * ------------
 *   ./client    - shared axios instance (baseURL, auth, error handling)
 *
 * DEPENDED ON BY
 * --------------
 *   frontend/src/pages/parameter-sets/ParameterSetsList.jsx
 *   frontend/src/pages/parameter-sets/ParameterSetForm.jsx
 *   frontend/src/pages/rfps/RFPAnalyze.jsx
 *                - calls listParameterSets() when rendering the dropdown
 *
 * @module api/parameterSetsApi
 * @author SPEQ55
 */

import { get, post, put, del } from './client';

/**
 * Lists parameter sets visible to the current user.
 *
 * @param {Object} [params]
 * @param {'org_default'|'private'|'shared'} [params.visibility] - Optional filter
 * @returns {Promise<Object[]>} Parameter set records
 */
export async function listParameterSets({ visibility } = {}) {
  const qs = visibility ? `?visibility=${encodeURIComponent(visibility)}` : '';
  const response = await get(`/parameter-sets${qs}`);
  return response.data.parameterSets;
}

/**
 * Gets a single parameter set by id.
 *
 * @param {string} id - Parameter set UUID
 * @returns {Promise<Object>} Parameter set record
 */
export async function getParameterSet(id) {
  const response = await get(`/parameter-sets/${id}`);
  return response.data.parameterSet;
}

/**
 * Creates a new parameter set.
 *
 * @param {Object} payload
 * @param {string} payload.name
 * @param {string} [payload.description]
 * @param {'org_default'|'private'|'shared'} payload.visibility
 * @param {string[]} [payload.keywords]
 * @param {string[]} [payload.technicalSpecs]
 * @returns {Promise<Object>} Created parameter set
 */
export async function createParameterSet(payload) {
  const response = await post('/parameter-sets', payload);
  return response.data.parameterSet;
}

/**
 * Updates a parameter set's name/description/keywords/specs.
 *
 * @param {string} id - Parameter set UUID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated parameter set
 */
export async function updateParameterSet(id, updates) {
  const response = await put(`/parameter-sets/${id}`, updates);
  return response.data.parameterSet;
}

/**
 * Soft-deletes a parameter set.
 *
 * @param {string} id - Parameter set UUID
 * @returns {Promise<void>}
 */
export async function deleteParameterSet(id) {
  await del(`/parameter-sets/${id}`);
}

/**
 * Flips a private parameter set to shared visibility.
 * Creator only.
 *
 * @param {string} id - Parameter set UUID
 * @returns {Promise<Object>} Updated parameter set
 */
export async function shareParameterSet(id) {
  const response = await post(`/parameter-sets/${id}/share`);
  return response.data.parameterSet;
}

/**
 * Flips a shared parameter set back to private visibility.
 * Creator only.
 *
 * @param {string} id - Parameter set UUID
 * @returns {Promise<Object>} Updated parameter set
 */
export async function unshareParameterSet(id) {
  const response = await post(`/parameter-sets/${id}/unshare`);
  return response.data.parameterSet;
}

/**
 * Promotes a parameter set to org_default visibility.
 * Org admin only.
 *
 * @param {string} id - Parameter set UUID
 * @returns {Promise<Object>} Updated parameter set
 */
export async function promoteParameterSet(id) {
  const response = await post(`/parameter-sets/${id}/promote`);
  return response.data.parameterSet;
}

export default {
  listParameterSets,
  getParameterSet,
  createParameterSet,
  updateParameterSet,
  deleteParameterSet,
  shareParameterSet,
  unshareParameterSet,
  promoteParameterSet,
};
